var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceLabelTypeEnum, ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import { SustainabilityIndex, SustainabilityIndexUtility } from '@inter-ikea-kompis/internal';
import RepairabilityIndexModalEnum from '../../enums/RepairabilityIndexModalEnum.js';
import RepairabilityIndexStyle from './RepairabilityIndexStyle.js';
/**
 * The Repairability Index component is used to communicate a product's repairability index. The size of the repairability index adapts to the font size and it should always be equivalent to the font size of the price.
 *
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */
export default class RepairabilityIndex extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Visible modal.
        this.visibleModal = null;
    }
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${RepairabilityIndexStyle.getStyle()}
			</style>
			${this.getSustainabilityIndexComponent()}
		`;
    }
    async connectedCallback() {
        this.customElements.define('kompis-sustainability-index', SustainabilityIndex);
        super.connectedCallback();
    }
    getSustainabilityIndexComponent() {
        const imageUrl = SustainabilityIndexUtility.getImageUrl(this.product, ProductComplianceLabelTypeEnum.repairabilityIndexImage);
        if (!imageUrl) {
            return this.getHiddenElement();
        }
        const technicalHeading = SustainabilityIndexUtility.getImageAltText(this.product, ProductComplianceTechnicalHeadingTypeEnum.repairabilityIndex);
        const documentUrl = SustainabilityIndexUtility.getDocumentUrl(this.product, ProductComplianceLabelTypeEnum.repairabilityIndexDocument);
        const documentImageUrl = SustainabilityIndexUtility.getDocumentUrlByFileType(this.product, 'svg', ProductComplianceLabelTypeEnum.repairabilityIndexDocument) ??
            SustainabilityIndexUtility.getDocumentUrlByFileType(this.product, 'png', ProductComplianceLabelTypeEnum.repairabilityIndexDocument);
        return html `<kompis-sustainability-index
			.product="${this.product}"
			.theme="${this.theme}"
			.disableLinks="${this.disableLinks}"
			.translations="${this.getTranslations()}"
			.visibleModal="${this.visibleModal}"
			.complianceLabelImageUrl="${imageUrl}"
			.complianceLabelDocumentUrl="${documentUrl}"
			.complianceLabelDocumentImageUrl="${documentImageUrl}"
			.complianceTechnicalHeading="${technicalHeading}"
			@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: RepairabilityIndexModalEnum.repairabilityIndexCard
            }
        }))}"
			@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', {
            bubbles: true,
            detail: {
                visibleModal: null
            }
        }))}"
		></kompis-sustainability-index>`;
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    /**
     * Used when nothing should be rendered.
     * The benefit compared to returning null, is that any padding/margin applied to this component by parents components is disregarded.
     *
     * @returns Template result for a hidden element.
     */
    getHiddenElement() {
        return html `
			<style>
				:host {
					display: none;
				}
			</style>
		`;
    }
}
__decorate([
    property({ type: Object, required: true })
], RepairabilityIndex.prototype, "theme", void 0);
__decorate([
    property({ type: Object, required: true })
], RepairabilityIndex.prototype, "product", void 0);
__decorate([
    property({ type: Object })
], RepairabilityIndex.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], RepairabilityIndex.prototype, "translations", void 0);
__decorate([
    property({ type: Boolean })
], RepairabilityIndex.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], RepairabilityIndex.prototype, "visibleModal", void 0);
//# sourceMappingURL=RepairabilityIndex.js.map